/**
 * Section "Expertises" de MasterClientDetail :
 *   - ExpertiseProfilesSection (orchestrateur)
 *   - ExpertiseActivationCard  (activer/désactiver fiscal/immeuble/autonome)
 *   - FiscalDossiersIndicator + ImmeubleDossiersIndicator (cartes vers entités séparées)
 *   - ProfileField + maskNas helpers
 *
 * Extrait de MasterClientDetail.jsx (Batch #14.221).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Loader2, Receipt, X, Building2, Briefcase, HandCoins, BookOpenCheck } from "lucide-react";
import { toast } from "@/lib/toast";
import { API, authH } from "@/api";


// =============================================================
//  Ajustement 2 — Affichage des fiches expertises enfants
//  (Adresses Immeubles…) — propres au volet, NE remontent PAS au Master.
//  Le volet Fiscal est une carte indicatrice qui mène vers /admin/fiscal-dossiers.
// =============================================================
function ExpertiseProfilesSection({ master }) {
  const exps = master.expertises || {};
  const fiscalActivated = exps.fiscal?.activated;
  const immeubleActivated = exps.immeuble?.activated;
  const paieActivated = exps.paie?.activated;
  // Batch #14.313 — « Entrepreneurs » (clé autonome) est fusionné dans Tenue de livres : un dossier autonome compte comme Tenue de livres
  const booksActivated = exps.tenue_livres?.activated || exps.autonome?.activated;
  // Batch #14.23 — Affiche les boutons d'activation si AUCUNE expertise active
  const noneActive = !fiscalActivated && !immeubleActivated && !paieActivated && !booksActivated;
  const allActive = fiscalActivated && immeubleActivated && paieActivated && booksActivated;

  return (
    <div className="space-y-3" data-testid="masters-expertise-profiles">
      {/* Batch #14.23 — Bloc activation expertises (visible si au moins une est inactive) */}
      {!allActive && (
        <ExpertiseActivationCard master={master} noneActive={noneActive} />
      )}

      {/* Carte Fiscal indicatrice — étape 4 : entité séparée */}
      {fiscalActivated && <FiscalDossiersIndicator masterId={master.id} />}

      {/* Carte Immeuble Déclaratif indicatrice — étape 2 (Immeuble) : entité séparée */}
      {immeubleActivated && <ImmeubleDossiersIndicator masterId={master.id} />}

      {/* Batch #14.250 — Carte Paie : accès direct au hub du dossier */}
      {paieActivated && <PaieIndicator masterId={master.id} />}

      {/* Batch #14.271 / #14.313 — Carte Tenue de livres (inclut le volet Entrepreneur) */}
      {booksActivated && <BooksIndicator masterId={master.id} />}
    </div>
  );
}

function BooksIndicator({ masterId }) {
  return (
    <div className="border border-teal-200 bg-teal-50/40 rounded-xl p-4" data-testid="masters-books-indicator">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-teal-100 text-teal-700 flex items-center justify-center flex-shrink-0">
            <BookOpenCheck className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-semibold text-teal-900">Tenue de livres — clients, fournisseurs, ventes, achats, volet Entrepreneur</h3>
            <p className="text-xs text-teal-700 mt-0.5">Encaissements, décaissements, grand livre, états financiers ; revenus, dépenses (reçus photo), kilométrage, TPS/TVQ et tuile « Entreprise » du portail.</p>
          </div>
        </div>
        <Link to={`/admin/tenue-livres?master_id=${masterId}`}
          className="text-xs px-3 py-1.5 rounded-lg bg-teal-600 text-white hover:bg-teal-700 inline-flex items-center gap-1"
          data-testid="masters-books-indicator-link">
          Ouvrir le module
        </Link>
      </div>
    </div>
  );
}

function PaieIndicator({ masterId }) {
  return (
    <div className="border border-orange-200 bg-orange-50/40 rounded-xl p-4" data-testid="masters-paie-indicator">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-orange-100 text-orange-700 flex items-center justify-center flex-shrink-0">
            <HandCoins className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-semibold text-orange-900">Paie — employés, périodes, bulletins, remises DAS</h3>
            <p className="text-xs text-orange-700 mt-0.5">Calcul 2026 (ARC / Revenu Québec), chèques, dépôt direct CPA-005 et CCQ.</p>
          </div>
        </div>
        <Link to={`/admin/paie?master_id=${masterId}`}
          className="text-xs px-3 py-1.5 rounded-lg bg-orange-600 text-white hover:bg-orange-700 inline-flex items-center gap-1"
          data-testid="masters-paie-indicator-link">
          Ouvrir le module
        </Link>
      </div>
    </div>
  );
}

function EntrepreneursIndicatorLegacy() {
  return null;
}

// =============================================================
//  Batch #14.23 — Carte "Activer une expertise"
//  Affiche les boutons Fiscal / Immeuble / Autonome (selon non-activés)
//  Cliquer = POST /masters/{id}/expertises/{key}/activate → débloque le Bureau
// =============================================================
function ExpertiseActivationCard({ master, noneActive }) {
  const [busy, setBusy] = useState(null);  // null | "fiscal" | "immeuble" | "autonome" | "deact-<key>"

  const exps = master.expertises || {};
  // Batch #14.313 — « Entrepreneurs » (autonome) n'est plus proposé : fusionné dans Tenue de livres
  const items = [
    { key: "fiscal", label: "Fiscal", icon: Receipt, activated: !!exps.fiscal?.activated, tone: "blue" },
    { key: "immeuble", label: "Immeuble", icon: Building2, activated: !!exps.immeuble?.activated, tone: "emerald" },
    { key: "paie", label: "Paie", icon: HandCoins, activated: !!exps.paie?.activated, tone: "orange" },
    { key: "tenue_livres", label: "Tenue de livres", icon: BookOpenCheck, activated: !!(exps.tenue_livres?.activated || exps.autonome?.activated), tone: "teal" },
  ];

  const handleActivate = async (key, label) => {
    if (!window.confirm(
      `Activer l'expertise « ${label} » sur ce dossier ?\n\nUn dossier Google Drive sera créé automatiquement. Le Bureau (Cockpit) sera ensuite débloqué pour cette expertise.`,
    )) return;
    setBusy(key);
    try {
      await axios.post(`${API}/masters/${master.id}/expertises/${key}/activate`, {}, { headers: authH() });
      toast.success(`Expertise « ${label} » activée — Le Bureau est maintenant disponible.`);
      window.location.reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'activation");
    } finally {
      setBusy(null);
    }
  };

  const handleDeactivate = async (key, label) => {
    if (!window.confirm(
      `Désactiver l'expertise « ${label} » sur ce dossier ?\n\n` +
      `• Le dossier Google Drive et les fichiers existants seront CONSERVÉS intacts\n` +
      `• Le Bureau (Cockpit) de cette expertise sera masqué\n` +
      `• Vous pourrez réactiver l'expertise à tout moment (les fichiers Drive seront retrouvés)\n\n` +
      `Aucune donnée ne sera supprimée. Confirmer ?`,
    )) return;
    setBusy(`deact-${key}`);
    try {
      await axios.post(`${API}/masters/${master.id}/expertises/${key}/deactivate`, {}, { headers: authH() });
      toast.success(`Expertise « ${label} » désactivée — Le dossier Drive reste intact.`);
      window.location.reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de la désactivation");
    } finally {
      setBusy(null);
    }
  };

  const tones = {
    blue: "border-blue-200 bg-blue-50/40 text-blue-700",
    emerald: "border-emerald-200 bg-emerald-50/40 text-emerald-700",
    amber: "border-amber-200 bg-amber-50/40 text-amber-700",
    orange: "border-orange-200 bg-orange-50/40 text-orange-700",
    teal: "border-teal-200 bg-teal-50/40 text-teal-700",
  };
  const buttonTones = {
    blue: "bg-blue-600 hover:bg-blue-700",
    emerald: "bg-emerald-600 hover:bg-emerald-700",
    amber: "bg-amber-600 hover:bg-amber-700",
    orange: "bg-orange-600 hover:bg-orange-700",
    teal: "bg-teal-600 hover:bg-teal-700",
  };

  return (
    <div className={`border rounded-xl p-4 ${noneActive ? "border-indigo-200 bg-indigo-50/40" : "border-slate-200 bg-slate-50/30"}`} data-testid="masters-expertise-activation">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 rounded-lg bg-white shadow-sm flex items-center justify-center shrink-0">
          <Briefcase className="w-5 h-5 text-indigo-600" />
        </div>
        <div>
          <h3 className="font-semibold text-slate-900">
            {noneActive ? "Activer une expertise" : "Gérer les expertises"}
          </h3>
          <p className="text-xs text-slate-600 mt-0.5">
            {noneActive
              ? "Choisissez les expertises à activer pour ce dossier. Cela débloquera le Bureau (Cockpit) avec son workspace Drive associé."
              : "Activez/désactivez les expertises au besoin. La désactivation conserve les fichiers Drive."}
          </p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {items.map((it) => {
          if (it.activated) {
            const deactBusy = busy === `deact-${it.key}`;
            return (
              <div
                key={it.key}
                className={`flex items-center gap-0 border rounded-lg overflow-hidden text-xs font-semibold ${tones[it.tone]}`}
                data-testid={`masters-expertise-active-${it.key}`}
              >
                <span className="px-3 py-1.5 inline-flex items-center gap-1.5">
                  <it.icon className="w-3.5 h-3.5" /> {it.label} activé ✓
                </span>
                <button
                  onClick={() => handleDeactivate(it.key, it.label)}
                  disabled={deactBusy}
                  title={`Désactiver ${it.label} (conserve Drive)`}
                  className="px-2 py-1.5 border-l border-current/30 text-current hover:bg-rose-50 hover:text-rose-700 transition disabled:opacity-50"
                  data-testid={`masters-expertise-deactivate-${it.key}-btn`}
                >
                  {deactBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
                </button>
              </div>
            );
          }
          return (
            <button
              key={it.key}
              onClick={() => handleActivate(it.key, it.label)}
              disabled={busy === it.key}
              className={`px-3 py-1.5 rounded-lg text-white text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed ${buttonTones[it.tone]}`}
              data-testid={`masters-expertise-activate-${it.key}-btn`}
            >
              {busy === it.key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <it.icon className="w-3.5 h-3.5" />}
              Activer {it.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Étape 4 — Carte qui pointe vers les vrais dossiers fiscaux (entité séparée).
function FiscalDossiersIndicator({ masterId }) {
  const [stats, setStats] = useState({ loading: true, count: 0, current: null });

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/fiscal-dossiers?master_id=${masterId}`, { headers: authH() });
        const items = r.data?.items || [];
        const current = items.find((d) => d.tax_year === new Date().getFullYear()) || items[0];
        setStats({ loading: false, count: items.length, current });
      } catch {
        setStats({ loading: false, count: 0, current: null });
      }
    })();
  }, [masterId]);

  return (
    <div className="border border-blue-200 bg-blue-50/40 rounded-xl p-4" data-testid="masters-fiscal-indicator">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0">
            <Receipt className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-semibold text-blue-900">Expertise Fiscale activée</h3>
            <p className="text-xs text-blue-700 mt-0.5">
              {stats.loading
                ? "Chargement…"
                : stats.count === 0
                ? "Aucun dossier fiscal créé pour ce Master."
                : `${stats.count} dossier(s) fiscal — Actif ${stats.current?.tax_year || ""}`}
            </p>
          </div>
        </div>
        <Link
          to={stats.current ? `/admin/fiscal-dossiers/${stats.current.id}` : "/admin/fiscal-dossiers"}
          className="text-xs px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 inline-flex items-center gap-1"
          data-testid="masters-fiscal-indicator-link"
        >
          {stats.current ? "Ouvrir le dossier" : "Voir les dossiers fiscaux"}
        </Link>
      </div>
    </div>
  );
}

// Étape 2 (Immeuble) — Carte qui pointe vers les vrais dossiers immeuble déclaratifs (entité séparée).
function ImmeubleDossiersIndicator({ masterId }) {
  const [stats, setStats] = useState({ loading: true, count: 0, current: null });

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/immeuble-dossiers?master_id=${masterId}`, { headers: authH() });
        const items = r.data?.items || [];
        // Le backend trie déjà par created_at DESC → items[0] = le plus récent
        setStats({ loading: false, count: items.length, current: items[0] || null });
      } catch {
        setStats({ loading: false, count: 0, current: null });
      }
    })();
  }, [masterId]);

  return (
    <div className="border border-emerald-200 bg-emerald-50/40 rounded-xl p-4" data-testid="masters-immeuble-indicator">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center flex-shrink-0">
            <Building2 className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-semibold text-emerald-900">Dossiers Immeubles — Déclaratif</h3>
            <p className="text-xs text-emerald-700 mt-0.5">
              {stats.loading
                ? "Chargement…"
                : stats.count === 0
                ? "Aucun dossier immeuble déclaratif pour ce Master."
                : `${stats.count} dossier(s) déclaratif(s) — entité séparée`}
            </p>
          </div>
        </div>
        <Link
          to={stats.current ? `/admin/immeuble-dossiers/${stats.current.id}` : "/admin/immeuble-dossiers"}
          className="text-xs px-3 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-1"
          data-testid="masters-immeuble-indicator-link"
        >
          {stats.current ? "Ouvrir le dossier" : "Voir les dossiers immeubles"}
        </Link>
      </div>
    </div>
  );
}

function ProfileField({ label, value, testid }) {
  return (
    <div>
      <div className="text-[10px] text-slate-500 uppercase tracking-wide font-semibold">{label}</div>
      <div className="text-sm text-slate-800 font-mono" data-testid={testid}>{value}</div>
    </div>
  );
}

function maskNas(nas) {
  if (!nas || nas.length < 4) return "—";
  // Format XXX-XXX-XXX, on masque les 6 premiers chiffres
  const digits = nas.replace(/[^0-9]/g, "");
  if (digits.length < 4) return nas;
  const last3 = digits.slice(-3);
  return `••• ••• ${last3}`;
}

export { ExpertiseProfilesSection, ExpertiseActivationCard, FiscalDossiersIndicator, ImmeubleDossiersIndicator, ProfileField, maskNas };
